import { children, createUniqueId, type JSX, Show } from 'solid-js';
import { useDialogTrap } from '../lib/dialog-trap';
import { useClosing, useScrollLock } from '../lib/overlay';
import { Button } from './ui/Button';

// Confirm dialog (spec §3.6, shadcn AlertDialog), drawn as the canvas panel: a
// head row (media tile + title) over a rule, the consequence, the ruled footer.
// No close X; the overlay does NOT close (destructive confirms stay deliberate)
// — Esc cancels via the trap, and Cancel is the first control, so it is what
// takes initial focus: an Enter pressed on open never confirms.

export type ConfirmDialogProps = {
  readonly open: boolean;
  readonly title: string;
  readonly desc?: string;
  /** Icon tile beside the title in the head row — pass a bare icon. */
  readonly media?: JSX.Element;
  readonly cancelLabel: string;
  readonly actionLabel: string;
  /** Tinted-red action (never solid red). */
  readonly destructive?: boolean;
  /**
   * What the action will do, itemised under the consequence (e.g. old → new).
   * Part of the accessible description with `desc`: the itemised changes are
   * what a confirm is FOR, so a screen reader hears them too.
   */
  readonly children?: JSX.Element;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
};

export function ConfirmDialog(props: ConfirmDialogProps): JSX.Element {
  let dialog: HTMLDivElement | undefined;
  useDialogTrap(
    () => props.open,
    () => dialog,
    () => props.onCancel(),
  );
  useScrollLock(() => props.open);
  const gate = useClosing(() => props.open, 120);
  const descId = createUniqueId();
  const detailId = createUniqueId();
  // Resolved ONCE: reading `props.children` twice (the describedby test and the
  // render) would build the itemised list twice.
  const detail = children(() => props.children);
  const describedBy = (): string | undefined => {
    const ids = [props.desc ? descId : null, detail() ? detailId : null].filter(
      (id): id is string => id !== null,
    );
    return ids.length > 0 ? ids.join(' ') : undefined;
  };

  return (
    <Show when={gate.visible()}>
      <div class="dialog" classList={{ 'dialog--closing': gate.closing() }}>
        <div class="dialog__overlay" />
        <div
          class="confirm__content"
          role="alertdialog"
          aria-modal="true"
          aria-label={props.title}
          aria-describedby={describedBy()}
          tabindex="-1"
          ref={dialog}
        >
          <header class="confirm__header">
            <Show when={props.media}>
              <span class="confirm__media" aria-hidden="true">
                {props.media}
              </span>
            </Show>
            <h2 class="confirm__title">{props.title}</h2>
          </header>
          <Show when={props.desc}>
            <p id={descId} class="confirm__desc">
              {props.desc}
            </p>
          </Show>
          <Show when={detail()}>
            <div id={detailId} class="confirm__detail">
              {detail()}
            </div>
          </Show>
          <div class="confirm__footer">
            <Button variant="outline" onClick={props.onCancel}>
              {props.cancelLabel}
            </Button>
            <Button
              variant={props.destructive ? 'destructive' : 'primary'}
              onClick={props.onConfirm}
            >
              {props.actionLabel}
            </Button>
          </div>
        </div>
      </div>
    </Show>
  );
}
